import { apiClient } from "./apiClient";

export interface UserWithOpenPositionDetail {
  user_id: string;
  user_email?: string;
  user_name?: string;
  job_status: string;
  error_code?: string;
  error_message?: string;
  error_category?: string;
  attempts: number;
}

export interface ExitSignalStatus {
  signal_id: string;
  symbol: string;
  side: string;
  dispatched_at: string | null;
  total_users: number;
  succeeded: number;
  failed: number;
  queued: number;
  running: number;
  users_with_open_positions: string[];
  users_with_open_positions_details?: UserWithOpenPositionDetail[];
}

export interface RetryExitJobsRequest {
  user_ids?: string[];
  force?: boolean;
}

export interface RetryExitJobsResponse {
  jobs_retried: number;
  errors: string[];
}

export interface ForceClosePositionsRequest {
  user_ids?: string[];
  reason?: string;
}

export interface ForceClosePositionsResponse {
  positions_closed: number;
  errors: string[];
}

export interface DeletePositionBySignalRequest {
  user_ids?: string[];
  position_ids?: string[];
  reason?: string;
}

export interface DeleteResponse {
  status: string;
  message: string;
  deleted: number;
  errors: string[];
}

export async function deletePositionsBySignal(
  signalId: string,
  request: DeletePositionBySignalRequest
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/${signalId}/positions`,
      { data: request }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete positions by signal: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface RecentExitSignalsResponse {
  signals: ExitSignalStatus[];
  total: number;
}

export async function getExitSignalStatus(
  signalId: string,
  checkDelayMinutes: number = 5
): Promise<ExitSignalStatus> {
  try {
    const response = await apiClient.get<ExitSignalStatus>(
      `/admin/exit-signals/${signalId}/status`,
      {
        params: { check_delay_minutes: checkDelayMinutes },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get exit signal status: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function retryExitJobs(
  signalId: string,
  request: RetryExitJobsRequest = {}
): Promise<RetryExitJobsResponse> {
  try {
    const response = await apiClient.post<RetryExitJobsResponse>(
      `/admin/exit-signals/${signalId}/retry`,
      request
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to retry exit jobs: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function forceClosePositions(
  signalId: string,
  request: ForceClosePositionsRequest = {}
): Promise<ForceClosePositionsResponse> {
  try {
    const response = await apiClient.post<ForceClosePositionsResponse>(
      `/admin/exit-signals/${signalId}/force-close`,
      request
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to force close positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function getRecentExitSignals(
  maxAgeHours: number = 24,
  checkDelayMinutes: number = 5,
  limit: number = 50
): Promise<RecentExitSignalsResponse> {
  try {
    const response = await apiClient.get<RecentExitSignalsResponse>(
      `/admin/exit-signals/recent`,
      {
        params: {
          max_age_hours: maxAgeHours,
          check_delay_minutes: checkDelayMinutes,
          limit,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get recent exit signals: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface OrphanedPositionItem {
  position_id: string;
  user_id: string;
  account_id: string;
  symbol: string;
  side: string;
  quantity: number;
  entry_price?: number;
  mark_price?: number;
  opened_at?: string;
  signal_id?: string;
  user_email?: string;
  user_name?: string;
}

export interface StuckJobItem {
  job_id: string;
  job_type: string;
  user_id: string;
  signal_id?: string;
  symbol?: string;
  status: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  last_error?: string;
  stream_id?: string;
  stream_key?: string;
  consumer_group?: string;
  idle_seconds?: number;
}

export interface OrphanedPositionsResponse {
  positions: OrphanedPositionItem[];
  total: number;
}

export interface StuckJobsResponse {
  jobs: StuckJobItem[];
  total: number;
}

export interface DeleteResponse {
  deleted: number;
  errors: string[];
}

export async function getOrphanedPositions(): Promise<OrphanedPositionsResponse> {
  try {
    const response = await apiClient.get<OrphanedPositionsResponse>(
      `/admin/exit-signals/orphaned-positions`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get orphaned positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function getStuckJobs(
  minIdleMinutes: number = 10
): Promise<StuckJobsResponse> {
  try {
    const response = await apiClient.get<StuckJobsResponse>(
      `/admin/exit-signals/stuck-jobs`,
      {
        params: {
          min_idle_minutes: minIdleMinutes,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get stuck jobs: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteOrphanedPosition(
  positionId: string
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/orphaned-positions/${positionId}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete orphaned position: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function forceRemoveManuallyClosedPosition(
  positionId: string
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.post<DeleteResponse>(
      `/admin/exit-signals/manually-closed-positions/${positionId}/force-remove`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to force remove manually closed position: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteAllOrphanedPositions(): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/orphaned-positions`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete all orphaned positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function cleanupCompletedPositions(
  daysOld: number = 30
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/cleanup-completed-positions`,
      {
        params: {
          days_old: daysOld,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to cleanup completed positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface ComprehensiveCleanupResponse {
  total_deleted: number;
  summary: Record<string, number | Record<string, number>>;
  errors: string[];
}

export interface CleanupPreviewResponse {
  total_to_delete: number;
  summary: Record<string, number | Record<string, number>>;
  errors: string[];
  dry_run: boolean;
}

export interface OperationalResidueCleanupResponse {
  total_targeted?: number;
  total_deleted?: number;
  summary: Record<string, unknown>;
  protected_tables: string[];
  history_protected: boolean;
  errors: string[];
  dry_run: boolean;
}

export async function previewCleanup(
  daysOld: number = 7,
  includePending: boolean = true,
  wipeAll: boolean = false
): Promise<CleanupPreviewResponse> {
  try {
    const response = await apiClient.get<CleanupPreviewResponse>(
      `/admin/exit-signals/comprehensive-cleanup/preview`,
      {
        params: {
          days_old: daysOld,
          include_pending: includePending,
          wipe_all: wipeAll,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to preview cleanup: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function previewOperationalResidueCleanup(
  maxAgeHours: number = 24,
  streamScanCount: number = 5000
): Promise<OperationalResidueCleanupResponse> {
  try {
    const response = await apiClient.get<OperationalResidueCleanupResponse>(
      `/admin/exit-signals/residue-cleanup/preview`,
      {
        params: {
          max_age_hours: maxAgeHours,
          stream_scan_count: streamScanCount,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to preview residue cleanup: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function operationalResidueCleanup(
  maxAgeHours: number = 24,
  streamScanCount: number = 5000
): Promise<OperationalResidueCleanupResponse> {
  try {
    const response = await apiClient.delete<OperationalResidueCleanupResponse>(
      `/admin/exit-signals/residue-cleanup`,
      {
        params: {
          max_age_hours: maxAgeHours,
          stream_scan_count: streamScanCount,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to execute residue cleanup: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function comprehensiveCleanup(
  daysOld: number = 7,
  includePending: boolean = true,
  wipeAll: boolean = false,
  dryRun: boolean = false
): Promise<ComprehensiveCleanupResponse> {
  try {
    const response = await apiClient.delete<ComprehensiveCleanupResponse>(
      `/admin/exit-signals/comprehensive-cleanup`,
      {
        params: {
          days_old: daysOld,
          include_pending: includePending,
          wipe_all: wipeAll,
          dry_run: dryRun,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to run comprehensive cleanup: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function cleanupAllOldData(
  daysOld: number = 30,
  includePending: boolean = false
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/cleanup-all-old-data`,
      {
        params: {
          days_old: daysOld,
          include_pending: includePending,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to cleanup all old data: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

/**
 * Delete old exit signals and their positions.
 * This endpoint finds all exit signals older than maxAgeHours and deletes:
 * - All positions related to each signal
 * - The signals themselves
 * - Related jobs and DLQ messages
 * 
 * @param maxAgeHours Delete signals older than this many hours (default: 6)
 * @returns DeleteResponse with deletion results
 */
export async function deleteOldExitSignalsAndPositions(
  maxAgeHours: number = 6
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/old/cleanup`,
      {
        params: {
          max_age_hours: maxAgeHours,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete old exit signals: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface SkippedPositionItem {
  position_id: string;
  user_id: string;
  account_id: string;
  symbol: string;
  side: string;
  quantity: number;
  entry_price?: number;
  mark_price?: number;
  opened_at?: string;
  entry_signal_id?: string;
  entry_signal_created_at?: string;
  exit_signal_id?: string;
  exit_signal_created_at?: string;
  skip_reason: string;
  user_email?: string;
  user_name?: string;
}

export interface SkippedPositionsResponse {
  positions: SkippedPositionItem[];
  total: number;
}

export interface InvalidPositionItem {
  position_id: string;
  user_id: string;
  account_id: string;
  symbol: string;
  side: string;
  quantity: number;
  entry_price?: number;
  state: string;
  opened_at?: string;
  entry_signal_id?: string;
  invalid_reason: string;
  has_redis_position: boolean;
  user_email?: string;
  user_name?: string;
  entry_order_id?: string;
}

export interface InvalidPositionsResponse {
  positions: InvalidPositionItem[];
  total: number;
}

export interface DeleteInvalidPositionResponse {
  success: boolean;
  position_id: string;
  db_deleted: boolean;
  redis_deleted: boolean;
  error?: string;
}

export async function getSkippedPositions(): Promise<SkippedPositionsResponse> {
  try {
    const response = await apiClient.get<SkippedPositionsResponse>(
      `/admin/exit-signals/skipped-positions`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get skipped positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteSkippedPosition(
  positionId: string
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/skipped-positions/${positionId}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete skipped position: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteAllSkippedPositions(): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/skipped-positions`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete all skipped positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function closeActiveExitTickets(
  signalId: string
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.post<DeleteResponse>(
      `/admin/exit-signals/close-active-tickets`,
      null,
      {
        params: { signal_id: signalId },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to close active exit tickets: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function getInvalidPositions(): Promise<InvalidPositionsResponse> {
  try {
    const response = await apiClient.get<InvalidPositionsResponse>(
      `/admin/exit-signals/invalid-positions`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get invalid positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteInvalidPosition(
  positionId: string,
  deleteFromRedis: boolean = true
): Promise<DeleteInvalidPositionResponse> {
  try {
    const response = await apiClient.post<DeleteInvalidPositionResponse>(
      `/admin/exit-signals/invalid-positions/delete`,
      {
        position_id: positionId,
        delete_from_redis: deleteFromRedis,
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete invalid position: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function archiveExitSignalsWithForce(
  request: ArchiveExitSignalsRequest,
  closeActiveTickets: boolean = false
): Promise<ArchiveExitSignalsResponse> {
  try {
    const response = await apiClient.post<ArchiveExitSignalsResponse>(
      `/admin/exit-signals/archive-with-force`,
      request,
      {
        params: { close_active_tickets: closeActiveTickets },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to archive exit signals with force: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteJobById(jobId: string): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete job by id: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteStuckJob(jobId: string): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/stuck-jobs/${jobId}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete stuck job: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface PendingOrderItem {
  order_id: string;
  symbol: string;
  status: string;
  side: string;
  type: string;
  price: string | number;
  amount: number;
  filled: number;
  remaining: number;
  age_minutes: number;
  timestamp: number;
  account_id: string;
  recommendation_id?: string;
  sniper_decision?: string;
}

export interface PendingOrdersResponse {
  pending_orders: PendingOrderItem[];
  total: number;
  timestamp: string;
  message: string;
}

export interface CancelPendingOrderRequest {
  order_id: string;
  symbol: string;
  account_id: string;
}

export async function getPendingOrders(): Promise<PendingOrdersResponse> {
  try {
    const response = await apiClient.get<PendingOrdersResponse>(
      "/admin/exit-signals/pending-orders"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get pending orders: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function cancelPendingOrder(
  request: CancelPendingOrderRequest
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.post<DeleteResponse>(
      "/admin/exit-signals/pending-orders/cancel",
      request
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to cancel pending order: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function cancelAllPendingOrders(): Promise<DeleteResponse> {
  try {
    const response = await apiClient.post<DeleteResponse>(
      "/admin/exit-signals/pending-orders/cancel-all"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to cancel all pending orders: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteAllStuckJobs(
  minIdleMinutes: number = 10
): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/stuck-jobs`,
      {
        params: {
          min_idle_minutes: minIdleMinutes,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete all stuck jobs: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function getAllPendingJobs(): Promise<StuckJobsResponse> {
  try {
    const response = await apiClient.get<StuckJobsResponse>(
      "/admin/exit-signals/pending-jobs/all"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get all pending jobs: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteAllPendingJobs(): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      "/admin/exit-signals/pending-jobs/all"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete all pending jobs: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface StuckPositionItem {
  source: string;
  position_id?: string;
  job_id?: string;
  order_id?: string;
  signal_id?: string;
  user_id?: string;
  account_id?: string;
  symbol?: string;
  side?: string;
  quantity?: number;
  entry_price?: number;
  status?: string;
  error_code?: string;
  error_message?: string;
  created_at?: string;
  user_email?: string;
  user_name?: string;
  job_type?: string;
  attempts?: number;
  max_attempts?: number;
  age_minutes?: number;
}

export interface AllStuckPositionsResponse {
  stuck_positions: StuckPositionItem[];
  total: number;
  by_source: {
    exit_signal: number;
    orphaned: number;
    stuck_job: number;
    pending_order: number;
  };
}

export async function getAllStuckPositions(): Promise<AllStuckPositionsResponse> {
  try {
    const response = await apiClient.get<AllStuckPositionsResponse>(
      "/admin/exit-signals/all-stuck-positions"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get all stuck positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface ArchiveExitSignalsRequest {
  stale_age_days?: number;
  symbol?: string;
  dry_run?: boolean;
  closed_reason?: string;
}

export interface ArchiveExitSignalsResponse {
  dry_run: boolean;
  total_found: number;
  can_archive: number;
  cannot_archive: number;
  archived: number;
  signals_to_archive: Array<{
    signal_id: string;
    symbol: string;
    dispatched_at: string | null;
    validation: any;
  }>;
  signals_cannot_archive: Array<{
    signal_id: string;
    symbol: string;
    dispatched_at: string | null;
    validation: any;
  }>;
  error?: string;
}

export interface ArchivedExitSignalItem {
  signal_id: string;
  symbol: string;
  side: string;
  dispatched_at: string | null;
  archived_at: string | null;
  closed_reason: string | null;
  status: string;
}

export interface GetArchivedExitSignalsResponse {
  items: ArchivedExitSignalItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export async function archiveExitSignals(
  request: ArchiveExitSignalsRequest = {}
): Promise<ArchiveExitSignalsResponse> {
  try {
    const response = await apiClient.post<ArchiveExitSignalsResponse>(
      "/admin/exit-signals/archive",
      request
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to archive exit signals: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function getArchivedExitSignals(
  params: {
    symbol?: string;
    status?: string;
    closed_reason?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  } = {}
): Promise<GetArchivedExitSignalsResponse> {
  try {
    const response = await apiClient.get<GetArchivedExitSignalsResponse>(
      "/admin/exit-signals/archived",
      { params }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get archived exit signals: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface ArchiveStuckExitSignalsRequest {
  signal_ids?: string[];
  symbols?: string[];
  dry_run?: boolean;
  delete_execution_jobs?: boolean;
}

export interface ArchiveStuckExitSignalsResponse {
  dry_run: boolean;
  signals_found: number;
  signals_archived: number;
  execution_jobs_deleted: number;
  signal_ids: string[];
  errors: string[];
}

export async function archiveStuckExitSignals(
  request: ArchiveStuckExitSignalsRequest = {}
): Promise<ArchiveStuckExitSignalsResponse> {
  try {
    const response = await apiClient.post<ArchiveStuckExitSignalsResponse>(
      "/admin/exit-signals/archive-stuck",
      request
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to archive stuck exit signals: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export interface DeleteStuckPositionRequest {
  job_id?: string;
  signal_id?: string;
  symbol?: string;
  user_email?: string;
  dry_run?: boolean;
}

export interface DeleteStuckPositionResponse {
  dry_run: boolean;
  found: {
    execution_jobs: number;
    signal_events: number;
    user_positions: number;
    trade_logs: number;
    exit_tickets: number;
    redis_positions: number;
    redis_stream_entries: number;
  };
  deleted: {
    execution_jobs: number;
    signal_events: number;
    user_positions: number;
    trade_logs: number;
    exit_tickets: number;
    redis_positions: number;
    redis_stream_entries: number;
  };
  errors: string[];
}

export async function deleteStuckPosition(
  request: DeleteStuckPositionRequest
): Promise<DeleteStuckPositionResponse> {
  try {
    const response = await apiClient.post<DeleteStuckPositionResponse>(
      "/admin/exit-signals/delete-stuck-position",
      request
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete stuck position: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

// ============================================================================
// OLD TEST POSITIONS MANAGEMENT
// ============================================================================

export interface OldTestPositionItem {
  symbol: string;
  has_redis_position: boolean;
  has_tradelog: boolean;
  has_userposition: boolean;
  has_entry_signal: boolean;
  entry_signal_id?: string | null;
  entry_signal_age_days?: number | null;
  redis_positions_count: number;
  tradelog_count: number;
  userposition_count: number;
}

export interface OldTestPositionsResponse {
  positions: OldTestPositionItem[];
  total: number;
}

export async function getOldTestPositions(): Promise<OldTestPositionsResponse> {
  try {
    const response = await apiClient.get<OldTestPositionsResponse>(
      "/admin/exit-signals/old-test-positions"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get old test positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteOldTestPosition(symbol: string): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/old-test-positions/${encodeURIComponent(symbol)}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete old test position: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteAllOldTestPositions(): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      "/admin/exit-signals/old-test-positions"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete all old test positions: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

// ============================================================================
// PENDING SIGNALS MANAGEMENT
// ============================================================================

export interface PendingSignalItem {
  signal_id: string;
  symbol: string;
  action: string;
  side: string;
  status: string;
  created_at: string;
  dispatched_at?: string | null;
  age_hours: number;
  payload: Record<string, any>;
}

export interface PendingSignalsResponse {
  signals: PendingSignalItem[];
  total: number;
}

export async function getPendingSignals(): Promise<PendingSignalsResponse> {
  try {
    const response = await apiClient.get<PendingSignalsResponse>(
      "/admin/exit-signals/pending-signals"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get pending signals: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deletePendingSignal(signalId: string): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      `/admin/exit-signals/pending-signals/${signalId}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete pending signal: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function deleteAllPendingSignals(): Promise<DeleteResponse> {
  try {
    const response = await apiClient.delete<DeleteResponse>(
      "/admin/exit-signals/pending-signals"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to delete all pending signals: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

// ============================================================================
// REDIS STREAM JANITOR API
// ============================================================================

export interface StreamStatsResponse {
  success: boolean;
  timestamp: string;
  streams: Record<string, {
    xlen: number;
    maxlen: number;
    pending?: number;
    consumer_group?: string;
    error?: string;
  }>;
  error?: string;
}

export interface StreamTrimResponse {
  success: boolean;
  timestamp: string;
  total_trimmed: number;
  cumulative_trimmed: number;
  streams: Record<string, {
    stream_key: string;
    maxlen: number;
    trimmed: number;
    before_length: number;
    after_length: number;
    success: boolean;
    error?: string;
  }>;
  error?: string;
}

export interface StreamPurgeRequest {
  stream_key: string;
  consumer_group?: string;
  confirm: boolean;
}

export interface StreamPurgeResponse {
  success: boolean;
  stream_key: string;
  deleted: boolean;
  recreated: boolean;
  consumer_group_recreated: boolean;
  error?: string;
}

export async function getRedisStreamStats(): Promise<StreamStatsResponse> {
  try {
    const response = await apiClient.get<StreamStatsResponse>(
      "/admin/exit-signals/redis-streams/stats"
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to get stream stats: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function trimRedisStreams(maxlen?: number): Promise<StreamTrimResponse> {
  try {
    const url = maxlen 
      ? `/admin/exit-signals/redis-streams/trim?maxlen=${maxlen}`
      : "/admin/exit-signals/redis-streams/trim";
    const response = await apiClient.post<StreamTrimResponse>(url);
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to trim streams: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

export async function purgeRedisStream(request: StreamPurgeRequest): Promise<StreamPurgeResponse> {
  try {
    const response = await apiClient.delete<StreamPurgeResponse>(
      "/admin/exit-signals/redis-streams/purge",
      { data: request }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      `Failed to purge stream: ${error?.response?.status || "Unknown error"}`;
    const newError = new Error(errorMessage) as Error & { statusCode?: number };
    newError.statusCode = error?.response?.status;
    throw newError;
  }
}

